import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api';

// Initial State
const initialState = {
    patients: [],
    currentPatient: null,
    isLoading: false,
    error: null,
};

/* ==============================
   ASYNC THUNKS
============================== */

// Fetch all patients
export const fetchPatients = createAsyncThunk(
    'patients/fetchPatients',
    async (_, { rejectWithValue }) => {
        try {
            const response = await api.get('/patients');
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch patients');
        }
    }
);

// Fetch single patient by ID
export const fetchPatient = createAsyncThunk(
    'patients/fetchPatient',
    async (id, { rejectWithValue }) => {
        try {
            const response = await api.get(`/patients/${id}`);
            return response.data;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch patient');
        }
    }
);

// Create new patient
export const createPatient = createAsyncThunk(
    'patients/createPatient',
    async (patientData, { rejectWithValue }) => {
        try {
            const response = await api.post('/patients', patientData);
            return response.data.patient;
        } catch (error) {
            if (error.response?.status === 422) {
                return rejectWithValue(error.response.data.errors);
            }
            return rejectWithValue(error.response?.data?.message || 'Failed to create patient');
        }
    }
);

// Update patient
export const updatePatient = createAsyncThunk(
    'patients/updatePatient',
    async ({ id, patientData }, { rejectWithValue }) => {
        try {
            const response = await api.put(`/patients/${id}`, patientData);
            return response.data.patient;
        } catch (error) {
            if (error.response?.status === 422) {
                return rejectWithValue(error.response.data.errors);
            }
            return rejectWithValue(error.response?.data?.message || 'Failed to update patient');
        }
    }
);

// Delete patient
export const deletePatient = createAsyncThunk(
    'patients/deletePatient',
    async (id, { rejectWithValue }) => {
        try {
            await api.delete(`/patients/${id}`);
            return id;
        } catch (error) {
            return rejectWithValue(error.response?.data?.message || 'Failed to delete patient');
        }
    }
);

/* ==============================
   SLICE
============================== */

const patientsSlice = createSlice({
    name: 'patients',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        setCurrentPatient: (state, action) => {
            state.currentPatient = action.payload;
        },
        clearCurrentPatient: (state) => {
            state.currentPatient = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // FETCH ALL
            .addCase(fetchPatients.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchPatients.fulfilled, (state, action) => {
                state.isLoading = false;
                state.patients = action.payload;
            })
            .addCase(fetchPatients.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // FETCH SINGLE
            .addCase(fetchPatient.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchPatient.fulfilled, (state, action) => {
                state.isLoading = false;
                state.currentPatient = action.payload;
            })
            .addCase(fetchPatient.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // CREATE
            .addCase(createPatient.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(createPatient.fulfilled, (state, action) => {
                state.isLoading = false;
                state.patients.unshift(action.payload);
            })
            .addCase(createPatient.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // UPDATE
            .addCase(updatePatient.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(updatePatient.fulfilled, (state, action) => {
                state.isLoading = false;
                const index = state.patients.findIndex((p) => p.id === action.payload.id);
                if (index !== -1) state.patients[index] = action.payload;
                if (state.currentPatient?.id === action.payload.id) {
                    state.currentPatient = action.payload;
                }
            })
            .addCase(updatePatient.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // DELETE
            .addCase(deletePatient.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(deletePatient.fulfilled, (state, action) => {
                state.isLoading = false;
                state.patients = state.patients.filter((p) => p.id !== action.payload);
                if (state.currentPatient?.id === action.payload) {
                    state.currentPatient = null;
                }
            })
            .addCase(deletePatient.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            });
    },
});

export const { clearError, setCurrentPatient, clearCurrentPatient } = patientsSlice.actions;
export default patientsSlice.reducer;
